/**
 * wallos.io homepage: Ceiling Reading. Deliberately dependency-light (no router
 * hooks, no framer-motion, no providers) so it is the first thing that paints,
 * gated or not. The full Wall OS landing lives at /os.
 */

import React, { useEffect, useRef, useState } from 'react';
import { PERFORMANCE_HOURS, READING_HOURS } from '../../lib/voiceAllowance';

const PLAY_URL = 'https://play.google.com/store/apps/details?id=io.wallos.app';
const SERIF = { fontFamily: '"Fraunces", Georgia, "Times New Roman", serif' };

type Status = 'idle' | 'sending' | 'done' | 'error';

function detectSource(): string {
  try {
    const p = new URLSearchParams(window.location.search);
    const utm = p.get('utm_source') || p.get('source') || p.get('ref');
    return utm ? `tester:${utm}`.slice(0, 64) : 'tester-invite';
  } catch {
    return 'tester-invite';
  }
}

const STEPS = [
  {
    n: '1',
    title: 'Aim a light at the ceiling',
    body: 'Any mini projector, TV box or phone on a stand. No projector? Read right on your phone or tablet.',
  },
  {
    n: '2',
    title: 'Pick a bedtime story',
    body: 'Choose from the free classics or your own DRM-free books. Scan the code on the ceiling and your phone becomes the remote.',
  },
  {
    n: '3',
    title: 'Lie back and drift off',
    body: 'A calm voice reads aloud while each word glows overhead. The sleep timer fades it out when you nod off.',
  },
];

const FEATURES = [
  { icon: '📚', title: 'Free classics', body: 'Austen, Dickens, Carroll, Grimm and 77,000+ public-domain books from Project Gutenberg. Free voices, unlimited hours.' },
  { icon: '📱', title: 'Phone remote', body: 'Play, pause, skip chapters, change speed or voice from bed. No account needed: scan, tap, done.' },
  { icon: '🌙', title: 'Sleep timer', body: '15 to 90 minutes, with a gentle fade over the last minute. Night colours keep the room amber and dim.' },
  { icon: '🔒', title: 'Private uploads', body: 'Add your own DRM-free EPUB, PDF or TXT. Only you can see or play them. Never shared, never listed.' },
  { icon: '✨', title: 'Every word lights up', body: 'The spoken word is highlighted in time with the voice, so little ones can follow along.' },
  { icon: '↺', title: 'Picks up where you left off', body: 'Your place is saved every few seconds and syncs to any phone, tablet or ceiling.' },
];

const SHOTS = [
  { src: '/brand/phone-01-shelf.webp', alt: 'The bookshelf of free classics' },
  { src: '/brand/phone-03-ceiling.webp', alt: 'Every word lights up on the ceiling' },
  { src: '/brand/phone-04-remote.webp', alt: 'Your phone is the remote' },
  { src: '/brand/phone-05-sleep.webp', alt: 'Sleep timer with fade-out' },
  { src: '/brand/phone-06-uploads.webp', alt: 'Private uploads of your own books' },
];

const PLANS = [
  {
    name: 'Core',
    price: 'Free',
    note: 'forever',
    highlight: false,
    items: ['All the free classics', 'Free voices, unlimited hours', 'Phone remote, sleep timer, night colours', '3 private uploads'],
  },
  {
    name: 'Pro',
    price: '$7.99',
    note: '/ month',
    highlight: true,
    items: ['Everything in Core', `${READING_HOURS.PRO} h/month of premium narration`, `or ${PERFORMANCE_HOURS.PRO} h of the expressive performance voice (one shared voice budget)`, '30 private uploads'],
  },
  {
    name: 'Elite',
    price: '$16.99',
    note: '/ month',
    highlight: false,
    items: ['Everything in Pro', `${READING_HOURS.ELITE} h/month of premium narration`, `or ${PERFORMANCE_HOURS.ELITE} h of the expressive performance voice (one shared voice budget)`, '100 private uploads'],
  },
];

function Stars() {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
      <div
        className="absolute inset-0"
        style={{
          backgroundImage:
            'radial-gradient(1.5px 1.5px at 12% 18%, rgba(255,236,200,.8), transparent 60%),' +
            'radial-gradient(1px 1px at 28% 62%, rgba(255,236,200,.6), transparent 60%),' +
            'radial-gradient(1.5px 1.5px at 44% 12%, rgba(255,236,200,.7), transparent 60%),' +
            'radial-gradient(1px 1px at 63% 38%, rgba(255,236,200,.5), transparent 60%),' +
            'radial-gradient(1.5px 1.5px at 78% 22%, rgba(255,236,200,.8), transparent 60%),' +
            'radial-gradient(1px 1px at 88% 70%, rgba(255,236,200,.5), transparent 60%),' +
            'radial-gradient(1px 1px at 6% 84%, rgba(255,236,200,.5), transparent 60%),' +
            'radial-gradient(1.5px 1.5px at 52% 88%, rgba(255,236,200,.4), transparent 60%)',
        }}
      />
      <div
        className="absolute -top-40 left-1/2 h-[520px] w-[820px] -translate-x-1/2 rounded-full opacity-60 blur-3xl"
        style={{ background: 'radial-gradient(closest-side, rgba(245,185,90,.22), transparent)' }}
      />
    </div>
  );
}

function PlayBadge({ className = '' }: { className?: string }) {
  return (
    <a
      href={PLAY_URL}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center gap-3 rounded-2xl bg-black px-5 py-3 text-white ring-1 ring-white/20 transition hover:ring-amber-300/60 ${className}`}
    >
      <svg viewBox="0 0 24 24" className="h-7 w-7" aria-hidden>
        <path fill="#34A853" d="M3.6 1.8 13.4 12l-9.8 10.2c-.4-.2-.6-.7-.6-1.2V3c0-.5.2-1 .6-1.2Z" />
        <path fill="#FBBC04" d="m16.8 8.6-3.4 3.4 3.4 3.4 3.8-2.2c1-.6 1-1.8 0-2.4l-3.8-2.2Z" />
        <path fill="#4285F4" d="M3.6 22.2 13.4 12l3.4 3.4-11.6 6.7c-.6.4-1.2.4-1.6.1Z" />
        <path fill="#EA4335" d="M3.6 1.8c.4-.3 1-.3 1.6.1l11.6 6.7-3.4 3.4L3.6 1.8Z" />
      </svg>
      <span className="text-left leading-tight">
        <span className="block text-[10px] uppercase tracking-[0.18em] text-white/70">Get it on</span>
        <span className="block text-lg font-semibold">Google Play</span>
      </span>
    </a>
  );
}

function IosBadge() {
  return (
    <a
      href="#invite"
      className="inline-flex items-center gap-3 rounded-2xl bg-white/5 px-5 py-3 text-white ring-1 ring-white/15 transition hover:bg-white/10"
    >
      <svg viewBox="0 0 24 24" className="h-7 w-7 fill-white" aria-hidden>
        <path d="M16.4 12.6c0-2.6 2.1-3.8 2.2-3.9-1.2-1.8-3.1-2-3.7-2-1.6-.2-3.1.9-3.9.9-.8 0-2-.9-3.4-.9-1.7 0-3.3 1-4.2 2.6-1.8 3.1-.5 7.7 1.3 10.2.9 1.2 1.9 2.6 3.2 2.6 1.3-.1 1.8-.8 3.3-.8 1.6 0 2 .8 3.4.8 1.4 0 2.3-1.3 3.1-2.5 1-1.4 1.4-2.8 1.4-2.9 0 0-2.7-1-2.7-4.1ZM13.9 5c.7-.9 1.2-2 1-3.2-1 0-2.3.7-3 1.6-.7.8-1.2 2-1.1 3.1 1.2.1 2.3-.6 3.1-1.5Z" />
      </svg>
      <span className="text-left leading-tight">
        <span className="block text-[10px] uppercase tracking-[0.18em] text-white/60">iPhone · TestFlight</span>
        <span className="block text-lg font-semibold">Coming soon</span>
      </span>
    </a>
  );
}

function InviteForm() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [message, setMessage] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const value = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value)) {
      setStatus('error');
      setMessage('That email does not look right.');
      inputRef.current?.focus();
      return;
    }
    setStatus('sending');
    try {
      const res = await fetch('/api/waitlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: value, source: detectSource(), referrer: document.referrer || null }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || `Something went wrong (${res.status}).`);
      setStatus('done');
      setMessage(data.already ? 'You are already on the list. Your invite is on its way.' : 'You are on the list. We will email your tester invite.');
    } catch (err: any) {
      setStatus('error');
      setMessage(err?.message || 'Network hiccup. Try again in a moment.');
    }
  };

  if (status === 'done') {
    return (
      <div className="rounded-2xl border border-amber-300/30 bg-amber-300/10 px-6 py-5 text-amber-100" role="status">
        {message}
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-3 sm:flex-row" noValidate>
      <label htmlFor="invite-email" className="sr-only">Email</label>
      <input
        id="invite-email"
        ref={inputRef}
        type="email"
        inputMode="email"
        autoComplete="email"
        autoCapitalize="none"
        spellCheck={false}
        placeholder="you@email.com"
        value={email}
        onChange={(e) => {
          setEmail(e.target.value);
          if (status === 'error') setStatus('idle');
        }}
        disabled={status === 'sending'}
        className="flex-1 rounded-xl border border-white/15 bg-white/5 px-4 py-3.5 text-base text-white outline-none transition placeholder:text-slate-500 focus:border-amber-300 focus:bg-white/10 disabled:opacity-60"
      />
      <button
        type="submit"
        disabled={status === 'sending'}
        className="rounded-xl bg-amber-300 px-6 py-3.5 text-base font-bold text-[#1a1208] transition hover:bg-amber-200 active:scale-[0.98] disabled:opacity-60"
      >
        {status === 'sending' ? 'Sending…' : 'Get a tester invite'}
      </button>
      {status === 'error' && <p className="text-sm text-amber-400 sm:hidden">{message}</p>}
      {status === 'error' && <p className="sr-only" role="alert">{message}</p>}
    </form>
  );
}

export const ReadingHome: React.FC<{ unlocked: boolean }> = ({ unlocked }) => {
  useEffect(() => {
    document.title = 'WallOS: Bedtime stories on your ceiling';
  }, []);

  const primary = unlocked
    ? { href: '/read', label: 'Start reading free' }
    : { href: '#invite', label: 'Get a tester invite' };

  return (
    <div className="min-h-screen overflow-x-hidden bg-[#070b1f] text-slate-100 antialiased selection:bg-amber-300/30">
      <header className="sticky top-0 z-30 border-b border-white/5 bg-[#070b1f]/80 backdrop-blur">
        <nav className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3">
          <a href="/" className="flex items-center gap-2.5" aria-label="WallOS home">
            <img src="/brand/moonbook-96.webp" alt="" width={32} height={32} className="h-8 w-8 rounded-lg" />
            <span className="text-[17px] font-semibold tracking-tight">WallOS</span>
          </a>
          <div className="flex items-center gap-5 text-sm text-slate-300">
            <a href="#how" className="hidden hover:text-white sm:inline">How it works</a>
            <a href="#plans" className="hidden hover:text-white sm:inline">Plans</a>
            <a href={primary.href} className="rounded-full bg-amber-300 px-4 py-2 font-semibold text-[#1a1208] hover:bg-amber-200">
              {unlocked ? 'Open the shelf' : 'Tester invite'}
            </a>
          </div>
        </nav>
      </header>

      <main>
        <section className="relative">
          <Stars />
          <div className="relative mx-auto grid max-w-6xl items-center gap-10 px-5 pb-16 pt-12 md:grid-cols-[1.1fr_.9fr] md:pb-24 md:pt-20">
            <div>
              <p className="mb-4 text-[11px] font-bold uppercase tracking-[0.4em] text-amber-300">Ceiling Reading</p>
              <h1 className="text-[2.6rem] leading-[1.05] text-[#fbf3e4] sm:text-6xl" style={SERIF}>
                Bedtime stories <br className="hidden sm:block" />on your <span className="text-amber-300">ceiling</span>
              </h1>
              <p className="mt-5 max-w-xl text-lg leading-relaxed text-slate-300">
                Lie back and listen. A calm voice reads a classic aloud while every word glows above you.
                Your phone is the remote, and the sleep timer fades it out when you drift off.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <PlayBadge />
                <IosBadge />
              </div>
              <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
                <a href={primary.href} className="font-semibold text-amber-300 hover:text-amber-200">
                  {primary.label} →
                </a>
                <span className="text-slate-500">Android closed beta · free classics · no ads</span>
              </div>
            </div>
            <div className="relative mx-auto w-full max-w-[300px] md:max-w-[340px]">
              <div aria-hidden className="absolute -inset-10 rounded-full blur-3xl" style={{ background: 'radial-gradient(closest-side, rgba(245,185,90,.28), transparent)' }} />
              <img
                src="/brand/phone-03-ceiling-lg.webp"
                alt="WallOS reading A Christmas Carol, the spoken word highlighted"
                width={720}
                height={1280}
                fetchPriority="high"
                className="relative w-full rounded-[2rem] shadow-2xl shadow-black/60 ring-1 ring-white/10"
              />
              <img
                src="/brand/sleepymoon.webp"
                alt=""
                width={140}
                height={140}
                className="absolute -bottom-6 -left-8 h-28 w-28 rounded-full shadow-xl ring-4 ring-[#070b1f] sm:h-36 sm:w-36"
              />
            </div>
          </div>
        </section>

        <section id="how" className="scroll-mt-20 border-t border-white/5 bg-[#0a1030] py-16 md:py-24">
          <div className="mx-auto max-w-6xl px-5">
            <h2 className="text-3xl text-[#fbf3e4] sm:text-4xl" style={SERIF}>How it works</h2>
            <ol className="mt-10 grid gap-6 md:grid-cols-3">
              {STEPS.map((s) => (
                <li key={s.n} className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-amber-300/15 font-semibold text-amber-300">{s.n}</span>
                  <h3 className="mt-4 text-lg font-semibold text-white">{s.title}</h3>
                  <p className="mt-2 leading-relaxed text-slate-400">{s.body}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="py-16 md:py-24">
          <div className="mx-auto max-w-6xl px-5">
            <h2 className="text-3xl text-[#fbf3e4] sm:text-4xl" style={SERIF}>Made for the last ten minutes of the day</h2>
            <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {FEATURES.map((f) => (
                <div key={f.title} className="rounded-2xl border border-white/10 bg-gradient-to-b from-white/[0.05] to-transparent p-6">
                  <span className="text-2xl" aria-hidden>{f.icon}</span>
                  <h3 className="mt-3 text-lg font-semibold text-white">{f.title}</h3>
                  <p className="mt-2 leading-relaxed text-slate-400">{f.body}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="mt-14 flex snap-x snap-mandatory gap-4 overflow-x-auto px-5 pb-4 md:justify-center" aria-label="App screenshots">
            {SHOTS.map((s) => (
              <img
                key={s.src}
                src={s.src}
                alt={s.alt}
                width={480}
                height={853}
                loading="lazy"
                decoding="async"
                className="w-[44vw] max-w-[200px] flex-none snap-center rounded-2xl ring-1 ring-white/10"
              />
            ))}
          </div>
        </section>

        <section id="plans" className="scroll-mt-20 border-t border-white/5 bg-[#0a1030] py-16 md:py-24">
          <div className="mx-auto max-w-6xl px-5">
            <h2 className="text-3xl text-[#fbf3e4] sm:text-4xl" style={SERIF}>Plans</h2>
            <p className="mt-3 text-slate-400">Reading is free. Upgrade only if you want premium voices.</p>
            <div className="mt-10 grid gap-5 md:grid-cols-3">
              {PLANS.map((p) => (
                <div
                  key={p.name}
                  className={`rounded-2xl p-6 ${p.highlight ? 'border-2 border-amber-300/70 bg-amber-300/[0.06]' : 'border border-white/10 bg-white/[0.03]'}`}
                >
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-white">{p.name}</h3>
                    {p.highlight && <span className="rounded-full bg-amber-300 px-2.5 py-0.5 text-xs font-bold text-[#1a1208]">Popular</span>}
                  </div>
                  <p className="mt-3">
                    <span className="text-4xl font-semibold text-white">{p.price}</span>
                    <span className="ml-1 text-slate-400">{p.note}</span>
                  </p>
                  <ul className="mt-5 space-y-2 text-slate-300">
                    {p.items.map((i) => (
                      <li key={i} className="flex gap-2"><span className="text-amber-300" aria-hidden>✓</span>{i}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <p className="mt-5 text-sm text-slate-500">During the beta every tester gets Elite voices and upload limits free.</p>
          </div>
        </section>

        <section id="invite" className="relative scroll-mt-20 py-16 md:py-24">
          <Stars />
          <div className="relative mx-auto grid max-w-4xl items-center gap-10 px-5 md:grid-cols-[auto_1fr]">
            <img src="/brand/sleepymoon.webp" alt="The WallOS sleepy moon reading a book" width={560} height={560} loading="lazy" className="mx-auto h-44 w-44 rounded-3xl md:h-56 md:w-56" />
            <div>
              <h2 className="text-3xl text-[#fbf3e4] sm:text-4xl" style={SERIF}>Become a tester</h2>
              <p className="mt-3 text-slate-300">
                We are in closed beta on Android, with iPhone on TestFlight next. Leave your email and we will send
                your invite: the Play test link and a code that opens the full reader in your browser.
              </p>
              <div className="mt-6"><InviteForm /></div>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-white/5 py-10">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 px-5 text-sm text-slate-500 sm:flex-row">
          <div className="flex items-center gap-2">
            <img src="/brand/moonbook-96.webp" alt="" width={20} height={20} className="h-5 w-5 rounded" />
            <span>© {new Date().getFullYear()} WallOS</span>
          </div>
          <nav className="flex flex-wrap items-center justify-center gap-5">
            <a href="/privacy" className="hover:text-slate-300">Privacy</a>
            <a href="/terms" className="hover:text-slate-300">Terms</a>
            <a href="/dmca" className="hover:text-slate-300">Copyright &amp; DMCA</a>
            <a href="/os" className="text-slate-600 hover:text-slate-400">WallOS for walls</a>
          </nav>
        </div>
      </footer>
    </div>
  );
};

export default ReadingHome;
